/**
 * Panel de reportes generados (Sprint 5 G).
 *
 * Tras cerrar y firmar el assessment, el worker encola y produce los
 * 3 reportes (pdf_executive, docx_technical, json_export) en segundo
 * plano. Este componente hace polling cada 2s a
 * GET /assessments/:id/reports y muestra el estado de cada uno hasta
 * que esten todos disponibles.
 *
 * Polling se detiene cuando los 3 estan listos o tras 60s (mostramos
 * fallback "se demoro mas de lo normal; refresca o intentalo luego").
 *
 * E3 H3.4 re-skin: Button E1, Loader2, CheckCircle2, aria-busy, role="status".
 * LÓGICA INTACTA: refresh(), polling con setInterval, tick states.
 *
 * TODO(deuda-tecnica): el tamaño de archivo no se muestra porque el
 * endpoint GET /assessments/:id/reports no expone file_size_bytes en el
 * schema actual (ReportItem). Para mostrarlo se requiere cambio de API/schema.
 * Cuando el backend lo exponga, añadir:
 *   `Descargar PDF · ${formatBytes(rep.file_size_bytes)}`
 * al texto del botón de descarga.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';

import {
  type ReportItem,
  type ReportKind,
  downloadReport,
  reportLabel,
  reportsApi,
} from '../api/reports.js';
import { Button } from './ui/Button.js';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;
const KINDS: ReportKind[] = ['pdf_executive', 'docx_technical', 'json_export'];

interface Props {
  assessmentId: string;
  /** Si false, omite el polling automatico (estado intermedio). */
  autoPoll?: boolean;
}

export function ReportsPanel({ assessmentId, autoPoll = true }: Props): JSX.Element {
  const [byKind, setByKind] = useState<Partial<Record<ReportKind, ReportItem>>>({});
  const [polling, setPolling] = useState(autoPoll);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedAt = useRef<number>(Date.now());

  async function refresh(): Promise<void> {
    try {
      const r = await reportsApi.list(assessmentId);
      const next: Partial<Record<ReportKind, ReportItem>> = {};
      for (const rep of r.reports) {
        // Si hay multiples del mismo kind (regeneraciones), quedarse con el mas reciente.
        const existing = next[rep.kind];
        if (!existing || new Date(rep.generated_at) > new Date(existing.generated_at)) {
          next[rep.kind] = rep;
        }
      }
      setByKind(next);
      setError(null);
      if (KINDS.every((k) => next[k])) {
        setPolling(false);
        return;
      }
      if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
        setPolling(false);
        setStale(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error consultando reportes');
    }
  }

  useEffect(() => {
    void refresh();
    if (!polling) return;
    const t = setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [assessmentId, polling]);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
      <header className="flex items-center justify-between">
        <h2 className="font-semibold text-cumple-primary-900">Reportes</h2>
        <div className="flex items-center gap-2">
          {/* Estado "generando" — WCAG 4.1.3: role="status" anuncia a AT */}
          {polling && (
            <span
              role="status"
              aria-live="polite"
              className="flex items-center gap-1.5 text-xs text-slate-500"
            >
              <Loader2
                className="h-3.5 w-3.5 animate-spin text-cumple-primary-600"
                aria-hidden="true"
              />
              Generando…
            </span>
          )}
          {stale && (
            /* Button E1 para Reintentar — min-h-[44px] por CVA */
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                startedAt.current = Date.now();
                setStale(false);
                setPolling(true);
              }}
            >
              Reintentar
            </Button>
          )}
        </div>
      </header>
      {error && (
        <p role="alert" className="text-sm text-cumple-danger-500">
          {error}
        </p>
      )}
      <ul className="divide-y divide-slate-200">
        {KINDS.map((kind) => {
          const rep = byKind[kind];
          return (
            <li key={kind} className="flex items-center gap-3 py-3 min-h-[64px]">
              {/* Ícono de estado — decorativo, el texto role="status" es el canal AT */}
              <div className="shrink-0">
                {rep ? (
                  <CheckCircle2 className="h-5 w-5 text-cumple-success-600" aria-hidden="true" />
                ) : (
                  <Loader2
                    className={`h-5 w-5 text-cumple-primary-600 ${polling ? 'animate-spin' : 'opacity-30'}`}
                    aria-hidden="true"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                {/* role="status" por ítem: anuncia "listo" o "generando" a AT — WCAG 4.1.3 */}
                <span
                  role="status"
                  aria-label={
                    rep ? `${reportLabel(kind)} listo` : `Generando ${reportLabel(kind)}…`
                  }
                  className="sr-only"
                />
                <p className="text-sm font-medium text-cumple-primary-900">{reportLabel(kind)}</p>
                <p className="font-mono text-[10px] text-slate-400">
                  {rep
                    ? `sha256: ${rep.sha256.slice(0, 16)}…`
                    : polling
                      ? 'Generando…'
                      : 'Pendiente'}
                </p>
              </div>
              {rep ? (
                /* Descarga autenticada: fetch+Blob en lugar de <a download> que no
                   envía el header Authorization y recibe 401 JSON del backend. */
                <Button
                  variant="primary"
                  size="sm"
                  aria-label={`Descargar ${reportLabel(kind)}`}
                  onClick={() => {
                    downloadReport(assessmentId, rep.id, kind).catch((err: unknown) => {
                      setError(err instanceof Error ? err.message : 'Error descargando reporte');
                    });
                  }}
                >
                  Descargar ↓
                </Button>
              ) : (
                /* Disabled mientras genera — aria-busy + aria-disabled — WCAG 4.1.2 */
                <Button
                  variant="primary"
                  size="sm"
                  disabled
                  aria-disabled="true"
                  aria-busy={polling}
                  title="Disponible al terminar la generación"
                >
                  {polling ? 'Generando…' : 'Pendiente'}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {stale && (
        <p className="text-xs text-slate-500">
          La generación está tardando más de lo habitual. Refresca la página en unos minutos o
          intenta de nuevo desde el detalle del assessment.
        </p>
      )}
    </section>
  );
}
